import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, Type, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@core/auth/auth.service';
import { PublicService } from '@public/public.service';
import { LayoutDrawerComponent } from '@shared/components/page-layout/drawers/layout-drawer.component';
import { PageLayoutService } from '@shared/components/page-layout/page-layout.service';
import { CreditCardDrawerData, ParentDrawerRequest } from '@shared/components/page-layout/page-layout.types';
import { RecaptchaSecureParams } from '@shared/models/domain/custom-views.model';
import { UserCreditCard } from '@shared/models/domain/domain.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { UserCreditCardRequest } from '@shared/models/request/user-credit-card-request.model';
import { BillingErrorResponse } from '@shared/models/response/billing-error-response.model';
import { SelectOption } from '@shared/models/shared/select-option.model';
import { AlertService } from '@shared/services/alert.service';
import { CustomerUserCreditCardService, ICreditCardService, UserCreditCardService } from '@shared/services/credit-card.service';
import { ReCaptchaV3Service } from '@core/recaptcha/recaptcha.service';
import { catchError, EMPTY, mergeMap, pairwise, startWith, tap } from 'rxjs';

/**
 * Gateway-agnostic credit card drawer behavior (sidemark assignment, nickname,
 * token fetch, persistence). Gateway-specific UI/tokenization is implemented by subclasses.
 * Never instantiated directly - decorated (matching LayoutDrawerComponent's own shape) only
 * because Angular requires an explicit Component/Directive decorator on any class in an
 * inheritance chain that uses DI/lifecycle hooks, and a Directive cannot extend a Component.
 */
@Component({ changeDetection: ChangeDetectionStrategy.Eager,
 template: '' })
export abstract class CreditCardDrawerBaseComponent extends LayoutDrawerComponent implements OnInit {
    protected formBuilder = inject(UntypedFormBuilder);
    protected layoutService = inject(PageLayoutService);
    protected adminCreditCardService = inject(UserCreditCardService);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected authService = inject(AuthService);
    protected alertService = inject(AlertService);
    protected reCaptchaV3Service = inject(ReCaptchaV3Service);
    protected customerCreditCardService = inject(CustomerUserCreditCardService);
    protected publicService = inject(PublicService);
    public dialog = inject(MatDialog);
    private readonly destroyRef = inject(DestroyRef);
    declare drawer: ParentDrawerRequest<CreditCardDrawerData>;
    creditCardValidationForm = this.formBuilder.group({
        projectIds: [[] as string[]],
        updateJobs: [false],
        updateProjects: [false],
        nickname: ['', []]
    });

    busy = false;
    assignToAllJobs = true;
    creditCardService: ICreditCardService;
    isCustomer = false;
    sidemarkOptions: SelectOption[] = [];
    setSideMarkIds = [];
    userCreditCard: UserCreditCard;
    showSidemark = false;

    /** Set from generateTokenReference() before the client-token fetch; undefined for gateways that don't need it (e.g. Braintree). */
    protected tokenReference: string | undefined;
    protected isPublicSidemark = false;
    protected publicSidemarkProjectId: string;
    protected publicSidemarkTokenHash: string;

    constructor() {
        super();
        this.title = 'Credit Card Details';
    }

    ngOnInit(): void {
        this.isCustomer = this.drawer?.data.isCustomer;
        this.assignToAllJobs = (this.drawer?.data?.assignToAllJobs ?? true);
        if (this.isCustomer) {
            this.creditCardService = this.customerCreditCardService;
        }
        else {
            this.creditCardService = this.adminCreditCardService;
        }

        this.userCreditCard = this.drawer?.data.userCreditCard;
        const isEditMode = !!this.userCreditCard?.id;
        this.setupGatewayFormControls(isEditMode);

        this.isPublicSidemark = !!(this.drawer?.data?.isPublicPage && this.drawer?.data?.projectId && this.drawer?.data?.tokenHash);
        this.publicSidemarkProjectId = this.drawer?.data?.projectId;
        this.publicSidemarkTokenHash = this.drawer?.data?.tokenHash;

        this.busy = true;

        this.requestClientToken();

        const projects = this.drawer?.data.projects;
        projects?.forEach(project => {
            if (project?.creditCardId === this.userCreditCard?.id && project?.id) {
                this.setSideMarkIds.push(project.id)
            }
        });
        this.sidemarkOptions = (projects?.map(p => {
            return { value: p.id, label: p.name };
        })) || [];

        if (isEditMode) {
            this.creditCardValidationForm.controls.nickname.setValue(this.userCreditCard.nickname);
            this.creditCardValidationForm.controls.projectIds.setValue(this.setSideMarkIds);
            this.creditCardValidationForm.controls.updateProjects.setValue(this.setSideMarkIds?.length ? true : false);
            this.showSidemark = this.setSideMarkIds?.length ? true : false;
            this.creditCardValidationForm.updateValueAndValidity();
            this.changeDetectorRef.detectChanges();
        }

        this.sidemarkOptions.unshift({ value: '', label: 'All Sidemarks' });
        this.creditCardValidationForm.controls.updateProjects.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value: boolean) => {
                this.showSidemark = value;
                this.changeDetectorRef.markForCheck();
            });

        this.creditCardValidationForm.controls.projectIds.valueChanges
            .pipe(
                startWith([] as string[]),
                pairwise(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([prev, next]: [string[], string[]]) => {
                const nextValueAllSelected = next?.findIndex(n => n === '') > -1;
                const prevValueAllSelected = prev?.findIndex(p => p === '') > -1;
                if (nextValueAllSelected && !prevValueAllSelected) {
                    this.creditCardValidationForm.controls.projectIds.setValue(this.sidemarkOptions.map(s => s.value as string), { emitEvent: false });
                }
                else if (nextValueAllSelected && next?.length < this.sidemarkOptions.length) {
                    this.creditCardValidationForm.controls.projectIds.setValue(next.filter(n => n !== ''), { emitEvent: false });
                }
                else if (!nextValueAllSelected && prevValueAllSelected && next?.length === this.sidemarkOptions.length - 1) {
                    const nonRemovableSidemarks = this.sidemarkOptions
                        .filter(option => this.isDisableSideMarkOption(String(option.value)))
                        .map(option => String(option.value));

                    this.creditCardValidationForm.controls.projectIds.setValue(nonRemovableSidemarks, { emitEvent: false });
                }
            });
    }

    /**
     * Fetches a client token and initializes the gateway widget. Extracted from ngOnInit so a
     * subclass can re-run it after a retryable save error (e.g. JPMorgan's consent-declined code)
     * to mount a fresh widget against a newly generated reference.
     */
    protected requestClientToken(): void {
        this.tokenReference = this.generateTokenReference();

        if (this.authService?.accessToken) {
            this.creditCardService.getClientToken(this.tokenReference).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(token => this.initializeGateway(token));
        } else {
            this.reCaptchaV3Service.execute('credit_card')
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    mergeMap((recaptchaToken) => {
                        const secureParams: RecaptchaSecureParams = { recaptchaToken };
                        if (this.tokenReference) {
                            secureParams.reference = this.tokenReference;
                        }
                        return this.customerCreditCardService.getClientTokenSecure(secureParams).pipe(
                            tap(token => this.initializeGateway(token)),
                            catchError(() => EMPTY),
                        );
                    }),
                    catchError(() => {
                        this.alertService.errorToast('Error', 'Error trying to verify request (reCaptcha v3).');
                        return EMPTY;
                    }),
                )
                .subscribe();
        }
    }

    onCancel(): void {
        this.creditCardValidationForm.reset();
        this.layoutService.closeParentDrawer();
    }

    onSubmit(): void {
        this.busy = true;
        if (this.userCreditCard?.id) {
            const request = {
                ...this.userCreditCard,
                nickname: this.creditCardValidationForm.controls.nickname.value as string,
                updateJobs: this.creditCardValidationForm.controls.updateJobs.value as boolean,
                updateProjects: this.creditCardValidationForm.controls.updateProjects.value as boolean,
                projectIds: (this.creditCardValidationForm.controls.projectIds.value as string[])?.filter(p => p !== '')
            };

            this.creditCardService.customUpdate(this.userCreditCard.id, request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
                this.creditCardValidationForm.reset();
                this.layoutService.setParentDrawerSubject({ component: this.drawerComponentType, model: res });
                this.layoutService.closeParentDrawer();
                this.changeDetectorRef.markForCheck();
                this.busy = false;
            });
            this.busy = false;
        }
        else {
            this.submitNewCard();
        }
    }

    isDisableSideMarkOption(value: string): boolean {
        return this.setSideMarkIds.find(option => option === value) ? true : false;
    }

    /**
     * The concrete class rendered (Braintree or JPMorgan), so events emitted from shared
     * base-class logic identify themselves the same way `CustomCreditCardDrawerComponent`
     * resolves for whichever gateway is active in this build.
     */
    protected get drawerComponentType(): Type<LayoutDrawerComponent> {
        return this.constructor as Type<LayoutDrawerComponent>;
    }

    protected persistNewCard(fields: { externalCardIdNonce: string; last4DigitsCC: string; cardType: string }): void {
        if (this.isPublicSidemark) {
            const request = {
                ...fields,
                nickname: this.creditCardValidationForm.controls.nickname.value as string,
            };

            this.publicService.addPublicProjectCreditCard(this.publicSidemarkProjectId, this.publicSidemarkTokenHash, request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (res) => {
                    this.creditCardValidationForm.reset();
                    this.layoutService.closeParentDrawer();
                    this.layoutService.setParentDrawerSubject({ component: this.drawerComponentType, model: res });
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.busy = false;
                    this.changeDetectorRef.detectChanges();
                }
            });
            this.changeDetectorRef.detectChanges();
            return;
        }

        const request: Partial<UserCreditCardRequest> = {
            ...fields,
            userId: this.drawer?.data?.userId,
            nickname: this.creditCardValidationForm.controls.nickname.value as string,
            updateJobs: this.creditCardValidationForm.controls.updateJobs.value as boolean,
            updateProjects: this.creditCardValidationForm.controls.updateProjects.value as boolean,
            projectIds: (this.creditCardValidationForm.controls.projectIds.value as string[])?.filter(p => p !== '')
        };

        const creditCard = request as UserCreditCardRequest;
        if (this.authService?.accessToken) {
            this.creditCardService.customCreate(creditCard)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    mergeMap((res) => {
                        const pageRequest = new PageRequest();
                        pageRequest.currentPage = 1;
                        pageRequest.itemsPerPage = 1000;
                        pageRequest.sort = 'dateCreated';
                        pageRequest.sortDir = 'desc';
                        if (!this.isCustomer) {
                            pageRequest.find = {};
                            pageRequest.find['userId'] = this.drawer?.data?.userId;
                        }

                        return this.creditCardService.get(pageRequest).pipe(
                            tap(() => {
                                this.creditCardValidationForm.reset();
                                this.layoutService.closeParentDrawer();
                                this.layoutService.setParentDrawerSubject({ component: this.drawerComponentType, model: res });
                                this.changeDetectorRef.markForCheck();
                            }),
                            catchError(() => EMPTY),
                        );
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.busy = false;
                        this.handleSaveCardError(error);
                        this.changeDetectorRef.detectChanges();
                        return EMPTY;
                    }),
                )
                .subscribe();
        } else {
            this.onCancel();
            this.layoutService.setParentDrawerSubject({ component: this.drawerComponentType, model: creditCard });
        }
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Billing errors return HTTP 422 with { code, message } (Braintree and JPMorgan alike, per
     * backend contract confirmed 2026-09-20). Default: surface the server-provided message, or a
     * generic fallback. A gateway overrides this to special-case its own named codes.
     */
    protected handleSaveCardError(error: HttpErrorResponse): void {
        const body = error?.error as BillingErrorResponse | undefined;
        this.alertService.errorToast('Error', body?.message || 'Error saving credit card. Please try again.');
    }

    /**
     * Reference passed as ?reference= to the client-token endpoint and echoed back as
     * externalCardIdNonce when the card is saved, so the backend can correlate an async
     * gateway notification to this card. Gateways that don't need this (Braintree) leave
     * the default no-op.
     */
    protected generateTokenReference(): string | undefined {
        return undefined;
    }

    /** Add/remove gateway-specific form controls; required only when adding a new card (not editing). */
    protected abstract setupGatewayFormControls(isEditMode: boolean): void;

    /** Called once a client token has been fetched from the backend. */
    protected abstract initializeGateway(token: string): void;

    /** Called from onSubmit() when adding a new card (not editing). */
    protected abstract submitNewCard(): void;
}
